import { useCallback, useEffect, useState } from "react";

import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import Spinner from "@excalidraw/excalidraw/components/Spinner";

import {
  checkAuth,
  deleteDesign,
  listDesigns,
  loadDesign,
  renameDesign,
  saveDesign,
  type Design,
} from "../data/cloudLibrary";

import "./CloudLibrary.scss";

interface CloudLibraryDialogProps {
  onClose: () => void;
  onLoad: (
    data: { elements: any[]; appState: any; files?: any },
    designId: string,
  ) => void;
  onGetCurrentData?: () => {
    elements: readonly any[];
    appState: any;
    files?: any;
  } | null;
}

export function CloudLibraryDialog({
  onClose,
  onLoad,
  onGetCurrentData,
}: CloudLibraryDialogProps) {
  const [designs, setDesigns] = useState<Design[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [expandedDesign, setExpandedDesign] = useState<string | null>(null);
  const [loadingDesign, setLoadingDesign] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");

  const fetchDesigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const authenticated = await checkAuth();
      setIsAuthenticated(authenticated);

      if (!authenticated) {
        setLoading(false);
        return;
      }

      const response = await listDesigns();
      setDesigns(response.designs);
    } catch (err: any) {
      setError(err.message || "Failed to load designs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDesigns();
  }, [fetchDesigns]);

  const handleSaveDesign = async () => {
    if (!onGetCurrentData) {
      setError("Save not available");
      return;
    }
    const data = onGetCurrentData();
    if (!data || data.elements.length === 0) {
      setError("Nothing to save - draw something first!");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const title = saveTitle.trim() || `Design ${designs.length + 1}`;
      await saveDesign(data, undefined, title);
      setSaveTitle("");
      await fetchDesigns();
    } catch (err: any) {
      setError(err.message || "Failed to save design");
    } finally {
      setSaving(false);
    }
  };

  const handleLoadDesign = async (designId: string, version?: number) => {
    setLoadingDesign(`${designId}-${version || "latest"}`);
    try {
      const response = await loadDesign(designId, version);
      onLoad(response.data, designId);
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to load design");
    } finally {
      setLoadingDesign(null);
    }
  };

  const handleDeleteVersion = async (designId: string, version: number) => {
    if (!window.confirm(`Delete version ${version}?`)) {
      return;
    }
    try {
      await deleteDesign(designId, version);
      await fetchDesigns();
    } catch (err: any) {
      setError(err.message || "Failed to delete version");
    }
  };

  const handleDeleteAllVersions = async (designId: string) => {
    if (
      !window.confirm(
        "Delete all versions of this design? This cannot be undone.",
      )
    ) {
      return;
    }
    try {
      await deleteDesign(designId, undefined, true);
      await fetchDesigns();
    } catch (err: any) {
      setError(err.message || "Failed to delete design");
    }
  };

  const handleRename = async (designId: string) => {
    if (!newTitle.trim()) {
      return;
    }
    try {
      await renameDesign(designId, newTitle.trim());
      setEditingTitle(null);
      setNewTitle("");
      await fetchDesigns();
    } catch (err: any) {
      setError(err.message || "Failed to rename design");
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const renderSignInPrompt = () => (
    <div className="cloud-library__signin">
      <h3>Sign in to access Cloud Library</h3>
      <p>Save your designs to the cloud and access them from anywhere.</p>
      <a href="/.auth/login/aad" className="cloud-library__signin-btn">
        Sign in with Microsoft
      </a>
      <a
        href="/.auth/login/github"
        className="cloud-library__signin-btn cloud-library__signin-btn--github"
      >
        Sign in with GitHub
      </a>
    </div>
  );

  const renderSaveSection = () => (
    <div className="cloud-library__save-section">
      <h4>Save Current Drawing</h4>
      <div className="cloud-library__save-form">
        <input
          type="text"
          placeholder="Design name (optional)"
          value={saveTitle}
          onChange={(e) => setSaveTitle(e.target.value)}
          className="cloud-library__save-input"
        />
        <button
          className="cloud-library__btn cloud-library__btn--primary"
          onClick={handleSaveDesign}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save to Cloud"}
        </button>
      </div>
    </div>
  );

  const renderDesignList = () => (
    <div className="cloud-library__list">
      {designs.length === 0 ? (
        <div className="cloud-library__empty">
          <p>No saved designs yet.</p>
          <p>Your designs will appear here when you save them to the cloud.</p>
        </div>
      ) : (
        designs.map((design) => (
          <div key={design.designId} className="cloud-library__design">
            <div
              className="cloud-library__design-header"
              onClick={() =>
                setExpandedDesign(
                  expandedDesign === design.designId ? null : design.designId,
                )
              }
            >
              <div className="cloud-library__design-info">
                {editingTitle === design.designId ? (
                  <input
                    type="text"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleRename(design.designId);
                      }
                      if (e.key === "Escape") {
                        setEditingTitle(null);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                    className="cloud-library__title-input"
                  />
                ) : (
                  <h4 className="cloud-library__design-title">
                    {design.title}
                  </h4>
                )}
                <span className="cloud-library__design-meta">
                  {design.versions.length} version
                  {design.versions.length !== 1 ? "s" : ""} • Updated{" "}
                  {formatDate(design.updatedAt)}
                </span>
              </div>
              <div className="cloud-library__design-actions">
                <button
                  className="cloud-library__btn cloud-library__btn--primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLoadDesign(design.designId);
                  }}
                  disabled={loadingDesign === `${design.designId}-latest`}
                >
                  {loadingDesign === `${design.designId}-latest`
                    ? "Loading..."
                    : "Open Latest"}
                </button>
                <button
                  className="cloud-library__btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingTitle(design.designId);
                    setNewTitle(design.title);
                  }}
                >
                  Rename
                </button>
                <button
                  className="cloud-library__btn cloud-library__btn--danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteAllVersions(design.designId);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>

            {expandedDesign === design.designId && (
              <div className="cloud-library__versions">
                <h5>Version History</h5>
                {design.versions
                  .sort((a, b) => b.version - a.version)
                  .map((version) => (
                    <div
                      key={version.version}
                      className="cloud-library__version"
                    >
                      <span className="cloud-library__version-info">
                        v{version.version} • {formatDate(version.timestamp)} •{" "}
                        {formatSize(version.size)}
                      </span>
                      <div className="cloud-library__version-actions">
                        <button
                          className="cloud-library__btn cloud-library__btn--small"
                          onClick={() =>
                            handleLoadDesign(design.designId, version.version)
                          }
                          disabled={
                            loadingDesign ===
                            `${design.designId}-${version.version}`
                          }
                        >
                          {loadingDesign ===
                          `${design.designId}-${version.version}`
                            ? "..."
                            : "Load"}
                        </button>
                        {design.versions.length > 1 && (
                          <button
                            className="cloud-library__btn cloud-library__btn--small cloud-library__btn--danger"
                            onClick={() =>
                              handleDeleteVersion(
                                design.designId,
                                version.version,
                              )
                            }
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  return (
    <Dialog
      onCloseRequest={onClose}
      title="Cloud Library"
      className="cloud-library-dialog"
    >
      <div className="cloud-library">
        {error && (
          <div className="cloud-library__error">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {loading ? (
          <div className="cloud-library__loading">
            <Spinner />
            <p>Loading your designs...</p>
          </div>
        ) : !isAuthenticated ? (
          renderSignInPrompt()
        ) : (
          <>
            {onGetCurrentData && renderSaveSection()}
            {renderDesignList()}
          </>
        )}

        <div className="cloud-library__footer">
          <button
            className="cloud-library__btn"
            onClick={fetchDesigns}
            disabled={loading}
          >
            Refresh
          </button>
          <button className="cloud-library__btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Dialog>
  );
}
